import type { User } from "oidc-client-ts";
import { useEffect, useMemo, type ReactNode } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";
import { FullPageMessage } from "../components/FullPageMessage";
import { useConfig } from "../config";

function safeReturnPath(state: unknown): string {
  return typeof state === "string" && state.startsWith("/app") ? state : "/app";
}

/** Cognito hosted sign-in via OIDC authorization code + PKCE; tokens stay in sessionStorage. */
export function AuthSetup({ children }: { children: ReactNode }) {
  const { cognito } = useConfig();
  const navigate = useNavigate();
  const settings = useMemo(
    () => ({
      authority: cognito.authority,
      client_id: cognito.clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: window.location.origin,
      response_type: "code",
      scope: "openid email profile",
    }),
    [cognito.authority, cognito.clientId],
  );

  return (
    <AuthProvider
      {...settings}
      onSigninCallback={(user: User | undefined) => {
        navigate(safeReturnPath(user?.state), { replace: true });
      }}
    >
      {children}
    </AuthProvider>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const returnTo = location.pathname + location.search;
  const shouldSignIn = !auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error;

  useEffect(() => {
    if (shouldSignIn) {
      void auth.signinRedirect({ state: returnTo });
    }
  }, [shouldSignIn, auth, returnTo]);

  if (auth.error) {
    return (
      <FullPageMessage title="We couldn't sign you in">
        <p className="muted">{auth.error.message}</p>
        <button type="button" className="btn btn-primary" onClick={() => void auth.signinRedirect({ state: returnTo })}>
          Try again
        </button>
      </FullPageMessage>
    );
  }
  if (!auth.isAuthenticated) {
    return <FullPageMessage title="Taking you to sign in…" busy />;
  }
  return <>{children}</>;
}

export function useSignOut(): () => Promise<void> {
  const auth = useAuth();
  const { cognito } = useConfig();
  return async () => {
    await auth.removeUser();
    // Cognito has no OIDC end_session endpoint; its /logout clears the hosted-UI session.
    const logout = new URL("/logout", cognito.domain);
    logout.searchParams.set("client_id", cognito.clientId);
    logout.searchParams.set("logout_uri", window.location.origin);
    window.location.assign(logout.toString());
  };
}
